import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/PasswordInput';
import { apiService } from '../services/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message || '';
  // Post-login destination (e.g. the SDK consent page round-trips through
  // /login?next=/sdk-auth?…). Relative paths only, so ?next can't open-redirect.
  const rawNext = new URLSearchParams(location.search).get('next') || '';
  const next = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/app';
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiService.getAccount().then(() => navigate(next, { replace: true })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiService.login(formData.email, formData.password);
      navigate(next);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        'Invalid email or password.'
      );
    } finally {
      setLoading(false);
    }
  };

  // No topRightCta: this IS the only way in while sign-up is closed, so the nav
  // has nowhere else to send anyone. AuthShell renders no nav button when it is
  // omitted.
  return (
    <AuthShell>
      <h1 className="auth-heading">
        Welcome <span className="auth-heading__accent">back</span>
      </h1>
      <p className="auth-subheading">Log in to your Qombra account.</p>

      {successMessage && <p className="auth-success">{successMessage}</p>}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-field">
          <label className="auth-label" htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="auth-input"
            placeholder="you@example.com"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="password">Password</label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            className="auth-input"
            placeholder="••••••••"
            value={formData.password}
            onChange={handleChange}
          />
        </div>

        {error && <p className="auth-error">{error}</p>}

        <div className="auth-submit">
          <Button type="submit" className="auth-btn-block" disabled={loading}>
            {loading ? 'Logging in…' : 'Log in'}
          </Button>
        </div>
      </form>

      <p className="auth-aside">
        <button type="button" className="auth-link" onClick={() => navigate('/forgot-password')}>
          Forgot password?
        </button>
      </p>
    </AuthShell>
  );
};

export default LoginPage;
